/**
 * THE AI SECOND OPINION, as it is recorded on the decision.
 *
 * ── WHY THIS IS IN THE DESIGN'S SIDEBAR SLOT ──
 *
 * The "Why this one was held" artboard puts a margin-band histogram here:
 * 1,045 past offers bucketed by margin, with the office's accept rate in each
 * band. That cannot be derived. The decisions endpoint returns rows, caps a
 * page at 200, offers no offset or cursor and no aggregation, so a histogram
 * over the whole history would mean paging the entire log in the browser —
 * and a histogram built from the 50 rows on screen would be a different
 * statistic wearing the same label, which is the one outcome worth avoiding
 * most on this screen.
 *
 * So the slot carries the richest thing that IS on every row: the reviewer's
 * verdict on the free text. The awaiting-review queue now carries the same
 * verdict as a badge — it is the one fact about a held offer that no rule can
 * see, so it belongs where the work is — and this panel is the long form of
 * it: the blurb, and the evidence the reviewer quoted. Both read their words
 * from `REVIEW_VERDICTS` in ../../decisionPresentation, so the queue and the
 * drill-down cannot come to say different things about one review.
 *
 * ── THE ONE THING THIS MUST NOT SAY ──
 *
 * `no_concern` IS NOT AN APPROVAL. The API contract says so in as many words:
 * it means the reviewer worked and found nothing in the text. Nothing was ever
 * approved because of it, and the copy never implies otherwise. `unavailable`
 * is a third state — the reviewer did not work — and is said as that rather
 * than folded into "nothing found".
 */

import React from 'react';
import { Badge } from 'react-bootstrap';
import { formatNumberOrDash } from '../../presentation';
import { concernList, reviewPresentation } from '../../decisionPresentation';

function AgentReview({ review }) {
  const verdict = reviewPresentation(review);

  if (!verdict) {
    return (
      <section className="auto-approval-panel">
        <h2 className="auto-approval-panel-title">The AI second opinion</h2>
        <p className="mb-0">
          No review is recorded. Every decision taken since the reviewer was
          added carries one, so this is a decision from before it existed.
        </p>
      </section>
    );
  }

  const concerns = concernList(review);

  return (
    <section className="auto-approval-panel">
      <h2 className="auto-approval-panel-title">The AI second opinion</h2>
      <p className="auto-approval-panel-blurb">
        Read on every booking, held or approved, because the rules and the
        reviewer do not look at the same fields.
      </p>

      <Badge bg={verdict.badge.bg} text={verdict.badge.text} className="sync-status">
        {verdict.badge.label}
      </Badge>
      <p className="auto-approval-panel-blurb mt-2">{verdict.blurb}</p>

      {concerns.length > 0 && (
        <ul className="auto-approval-concerns">
          {concerns.map((concern, index) => (
            <li
              // eslint-disable-next-line react/no-array-index-key
              key={`${concern.kind}-${index}`}
            >
              <span className="auto-approval-concern-kind">{concern.kind}</span>
              {concern.evidence && (
                <span className="auto-approval-concern-evidence">
                  {`“${concern.evidence}”`}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      <dl className="sync-decision-context mb-0">
        <dt>Guidance version</dt>
        <dd>{formatNumberOrDash(review.instructions_version, (v) => `v${v}`)}</dd>
        <dt>Model</dt>
        <dd>{review.model || 'Not recorded'}</dd>
        <dt>Took</dt>
        <dd>{formatNumberOrDash(review.ms, (ms) => `${ms} ms`)}</dd>
      </dl>
    </section>
  );
}

export default AgentReview;
